import financialApi from 'api/requests/financial'
import Loader from 'components/_UI/Loader';
import Paginate from 'components/_UI/Paginate';
import Text from 'components/_UI/Text'
import Ilustration from 'components/_UI/Ilustration';
import NotResultDashBoard from 'components/commons/NotResultDashBoard'
import React, { useEffect, useState } from 'react'
import { Activity, Calendar, TrendingUp, TrendingDown, Download } from 'lucide-react';
import styled from 'styled-components';
import { Column } from 'styled/alignment/Column';
import colors from 'theme/colors';
import fonts from 'theme/fonts';
import breakpoints from 'theme/breakpoints';
import currency from 'helpers/currency';

/* Status do pedido, não da transação: `transactions` não tem coluna de status,
   é o pedido que diz se aquele dinheiro vira saldo. */
const STATUS_MAP = {
    approved: { label: 'Aprovado', bg: '#DCFCE7', color: '#166534' },
    pending: { label: 'Aguardando pagamento', bg: '#FEF3C7', color: '#92400E' },
    canceled: { label: 'Expirado', bg: '#FEE2E2', color: '#DC2626' },
    refunded: { label: 'Estornado', bg: '#F3F4F6', color: '#374151' },
    refused: { label: 'Recusado', bg: '#FEE2E2', color: '#DC2626' },
    chargeback: { label: 'Chargeback', bg: '#FEE2E2', color: '#991B1B' },
    in_dispute: { label: 'Em disputa', bg: '#FEF3C7', color: '#92400E' },
};

const STATUS_OPTIONS = [
    { value: '', label: 'Todos os status' },
    { value: 'approved', label: 'Aprovado' },
    { value: 'pending', label: 'Aguardando pagamento' },
    { value: 'canceled', label: 'Expirado' },
    { value: 'refunded', label: 'Estornado' },
    { value: 'refused', label: 'Recusado' },
    { value: 'chargeback', label: 'Chargeback' },
];

const PAYMENT_OPTIONS = [
    { value: '', label: 'Todos os pagamentos' },
    { value: 'credit_card', label: 'Cartão de crédito' },
    { value: 'pix', label: 'PIX' },
    { value: 'free', label: 'Grátis' },
];

/* Só pedido aprovado entra no saldo; o resto é dinheiro que não chegou. */
const CREDITS_BALANCE = 'approved';

function TransactionIcon({ status }) {
    const Icon = status === CREDITS_BALANCE ? TrendingUp : TrendingDown;

    return (
        <IconCircle category={status === CREDITS_BALANCE ? 'sell' : 'refund'}>
            <Icon size={16} />
        </IconCircle>
    );
}

export default function TransactionsList({ filters = {} }) {
    const [content, setContent] = useState(null);
    const [paginate, setPaginate] = useState(null);
    const [loading, setLoading] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [exportError, setExportError] = useState(false);
    const [ownFilters, setOwnFilters] = useState({ status: '', payment_method: '' });

    /* Período e evento vêm da barra do topo da tela; status e forma de
       pagamento são desta aba. */
    const queryFilters = {
        ...filters,
        ...Object.fromEntries(Object.entries(ownFilters).filter(([, value]) => value)),
    };

    const queryKey = JSON.stringify(queryFilters);

    /* `period: 'all'` é o padrão, não um recorte: contá-lo como filtro faria o
       estado vazio culpar filtros que o usuário nunca escolheu. */
    const hasNarrowingFilter = Boolean(
        ownFilters.status || ownFilters.payment_method || filters?.event ||
        filters?.start_date || (filters?.period && filters.period !== 'all')
    );

    useEffect(() => {
        getTransactions();
    }, [queryKey])

    async function getTransactions(number = 1) {
        setLoading(true);
        try {
            const { data } = await financialApi.getOrderTransactions({ ...queryFilters, page: number })
            setContent(data?.data)
            setPaginate(data?.meta)
        } catch (error) {

        } finally {
            setLoading(false);
        }
    }

    async function exportTransactions() {
        setExporting(true);
        setExportError(false);
        try {
            const { data } = await financialApi.exportOrderTransactions(queryFilters);
            const url = window.URL.createObjectURL(data);
            const link = document.createElement('a');

            link.href = url;
            link.setAttribute('download', `transacoes-${new Date().toISOString().slice(0, 10)}.csv`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            /* Download é ação explícita do usuário: falhar em silêncio deixa
               ele clicando de novo sem saber o que aconteceu. */
            setExportError(true);
        } finally {
            setExporting(false);
        }
    }

    function changeOwnFilter(field, value) {
        setOwnFilters(previous => ({ ...previous, [field]: value }));
    }

    function formatDate(dateStr) {
        const d = new Date(dateStr);
        return d.toLocaleDateString('pt-BR');
    }

    function formatTime(dateStr) {
        const d = new Date(dateStr);
        return d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
    }

    return (
        <Container>
            <SectionHeader>
                <SectionIcon>
                    <Calendar size={18} color={colors.occasionalPurple} />
                </SectionIcon>
                <HeaderContent>
                    <Text type="bold" color={colors.nightDark}>
                        Transações dos pedidos
                    </Text>
                    <Text name="small" color={colors.night}>
                        Cada pedido vendido, com o que a plataforma reteve e o que sobra para
                        você. Só pedido aprovado vira saldo. Saques ficam na aba Saques.
                    </Text>
                </HeaderContent>

                <ExportButton
                    type="button"
                    onClick={exportTransactions}
                    disabled={exporting || !content?.length}
                    title={!content?.length ? 'Nada para exportar com os filtros atuais' : 'Baixar em CSV'}
                >
                    <Download size={15} />
                    <span>{exporting ? 'Gerando...' : 'Exportar CSV'}</span>
                </ExportButton>
            </SectionHeader>

            <FiltersRow>
                <FilterSelect
                    value={ownFilters.status}
                    onChange={(e) => changeOwnFilter('status', e.target.value)}
                    aria-label="Filtrar por status do pedido"
                >
                    {STATUS_OPTIONS.map(option => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                </FilterSelect>

                <FilterSelect
                    value={ownFilters.payment_method}
                    onChange={(e) => changeOwnFilter('payment_method', e.target.value)}
                    aria-label="Filtrar por forma de pagamento"
                >
                    {PAYMENT_OPTIONS.map(option => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                </FilterSelect>

                {exportError && (
                    <ExportError>Não foi possível gerar o arquivo. Tente novamente.</ExportError>
                )}
            </FiltersRow>
            {loading ? (
                <LoaderWrapper>
                    <Loader />
                </LoaderWrapper>
            ) : (
                content?.length > 0 ? (
                    <>
                        {/* Desktop table */}
                        <DesktopTable>
                            <thead>
                                <tr>
                                    <Th>Pedido</Th>
                                    <Th>Pagamento</Th>
                                    <Th>Valor bruto</Th>
                                    <Th>Taxa</Th>
                                    <Th>Valor líquido</Th>
                                    <Th>Status</Th>
                                </tr>
                            </thead>
                            <tbody>
                                {content.map(item => {
                                    const statusInfo = STATUS_MAP[item?.status] || STATUS_MAP.pending;
                                    const creditsBalance = item?.status === CREDITS_BALANCE;

                                    return (
                                        <Tr key={item?.id}>
                                            <Td>
                                                <DescriptionCell>
                                                    <TransactionIcon status={item?.status} />
                                                    <DescriptionContent>
                                                        <DescriptionText>
                                                            {item?.event_name || `Pedido #${item?.order_id}`}
                                                        </DescriptionText>
                                                        <DateSecondary>
                                                            #{item?.order_id} · {formatDate(item?.created_at)} - {formatTime(item?.created_at)}
                                                        </DateSecondary>
                                                    </DescriptionContent>
                                                </DescriptionCell>
                                            </Td>
                                            <Td>
                                                <ValueText>{item?.payment_method_label}</ValueText>
                                                {item?.installments > 1 && (
                                                    <DateSecondary>{item.installments}x</DateSecondary>
                                                )}
                                            </Td>
                                            <Td>
                                                <ValueText>{currency.format(item?.gross_value)}</ValueText>
                                            </Td>
                                            <Td>
                                                <ValueText color={colors.danger}>
                                                    -{currency.format(item?.tax_value)}
                                                </ValueText>
                                            </Td>
                                            <Td>
                                                <ValueText
                                                    style={{ fontWeight: 600 }}
                                                    color={creditsBalance ? colors.success : colors.nightLight}
                                                >
                                                    {currency.format(item?.net_value)}
                                                </ValueText>
                                            </Td>
                                            <Td>
                                                <StatusBadge bgColor={statusInfo.bg} textColor={statusInfo.color}>
                                                    {statusInfo.label}
                                                </StatusBadge>
                                            </Td>
                                        </Tr>
                                    );
                                })}
                            </tbody>
                        </DesktopTable>

                        {/* Mobile cards */}
                        <MobileCards>
                            {content.map(item => {
                                const statusInfo = STATUS_MAP[item?.status] || STATUS_MAP.pending;
                                const creditsBalance = item?.status === CREDITS_BALANCE;

                                return (
                                    <MobileCard key={item?.id}>
                                        <MobileCardHeader>
                                            <DescriptionCell>
                                                <TransactionIcon status={item?.status} />
                                                <DescriptionContent>
                                                    <DescriptionText>
                                                        {item?.event_name || `Pedido #${item?.order_id}`}
                                                    </DescriptionText>
                                                    <DateSecondary>
                                                        #{item?.order_id} · {formatDate(item?.created_at)} - {formatTime(item?.created_at)}
                                                    </DateSecondary>
                                                </DescriptionContent>
                                            </DescriptionCell>
                                        </MobileCardHeader>

                                        <MobileCardBody>
                                            <MobileField>
                                                <MobileLabel>Pagamento</MobileLabel>
                                                <MobileValue>
                                                    {item?.payment_method_label}
                                                    {item?.installments > 1 ? ` · ${item.installments}x` : ''}
                                                </MobileValue>
                                            </MobileField>
                                            <MobileField>
                                                <MobileLabel>Valor bruto</MobileLabel>
                                                <MobileValue>{currency.format(item?.gross_value)}</MobileValue>
                                            </MobileField>
                                            <MobileField>
                                                <MobileLabel>Taxa</MobileLabel>
                                                <ValueText color={colors.danger}>
                                                    -{currency.format(item?.tax_value)}
                                                </ValueText>
                                            </MobileField>
                                            <MobileField>
                                                <MobileLabel>Valor líquido</MobileLabel>
                                                <ValueText
                                                    style={{ fontWeight: 600 }}
                                                    color={creditsBalance ? colors.success : colors.nightLight}
                                                >
                                                    {currency.format(item?.net_value)}
                                                </ValueText>
                                            </MobileField>
                                            <MobileField>
                                                <MobileLabel>Status</MobileLabel>
                                                <StatusBadge bgColor={statusInfo.bg} textColor={statusInfo.color}>
                                                    {statusInfo.label}
                                                </StatusBadge>
                                            </MobileField>
                                        </MobileCardBody>
                                    </MobileCard>
                                );
                            })}
                        </MobileCards>

                        {paginate?.last_page > 1 && (
                            <PaginateWrapper>
                                <Paginate
                                    onPaginate={number => getTransactions(number)}
                                    perPage={paginate?.per_page}
                                    totalItems={paginate?.total}
                                    lastPage={paginate?.last_page}
                                    firstPage={1}
                                    currentPage={paginate?.current_page}
                                    marginTop={'0'}
                                />
                            </PaginateWrapper>
                        )}
                    </>
                ) : (
                    <EmptyState>
                        <Column width='auto' height='240px'>
                            <Ilustration name='transactions' />
                        </Column>
                        <Text marginTop='16px' name='highlight' type='bold' color={colors.nightDark}>
                            Nenhuma transação encontrada
                        </Text>
                        <Text name='small' color={colors.night} textAlign='center'>
                            {hasNarrowingFilter
                                ? 'Nenhuma venda ou estorno bate com os filtros selecionados.'
                                : 'Nenhuma venda liberada até agora. Elas aparecem aqui no dia em que o valor cai no seu saldo.'}
                        </Text>
                    </EmptyState>
                )
            )}
        </Container>
    )
}

/* ── Header ───────────────────────────────────────── */

const SectionHeader = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 12px;
    margin-bottom: 16px;
    padding: 0 16px;
    flex-wrap: wrap;

    @media screen and (min-width: ${breakpoints.md}) {
        padding: 0 24px;
        align-items: center;
        flex-wrap: nowrap;
    }
`;

const ExportButton = styled.button`
    display: flex;
    align-items: center;
    gap: 6px;
    margin-left: auto;
    padding: 8px 14px;
    border: 1px solid ${colors.neutralDark};
    border-radius: 8px;
    background: ${colors.neutralLight};
    color: ${colors.night};
    font-size: ${fonts.sizes.mini};
    font-family: inherit;
    white-space: nowrap;
    cursor: pointer;
    transition: background 0.15s ease;

    &:hover:not(:disabled) {
        background: ${colors.neutral};
    }

    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }

    &:focus-visible {
        outline: 2px solid ${colors.occasionalPurple};
        outline-offset: 2px;
    }
`;

const FiltersRow = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-bottom: 20px;
    padding: 0 16px;

    @media screen and (min-width: ${breakpoints.md}) {
        padding: 0 24px;
    }
`;

const ExportError = styled.span`
    align-self: center;
    font-size: ${fonts.sizes.mini};
    color: ${colors.danger};
`;

const FilterSelect = styled.select`
    padding: 7px 10px;
    border: 1px solid ${colors.neutralDark};
    border-radius: 8px;
    background: ${colors.neutralLight};
    color: ${colors.nightDark};
    font-size: ${fonts.sizes.mini};
    font-family: inherit;
    cursor: pointer;

    &:focus-visible {
        outline: 2px solid ${colors.occasionalPurple};
        outline-offset: -1px;
    }
`;

const SectionIcon = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border-radius: 8px;
    background: #F3EAFF;
    flex-shrink: 0;
`;

const HeaderContent = styled.div`
    flex: 1;
`;

/* ── Layout ────────────────────────────────────── */

const Container = styled.div`
    min-height: 300px;
    display: flex;
    flex-direction: column;
    background: ${colors.neutralLight};
    border: 1px solid ${colors.neutralDark};
    border-radius: 14px;
    padding-top: 20px;
    margin-top: 10px;
    overflow: hidden;
`;

const LoaderWrapper = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 60px 20px;
`;

const EmptyState = styled(Column)`
    align-items: center;
    justify-content: center;
    padding: 60px 20px;
`;

const PaginateWrapper = styled.div`
    padding: 16px 24px;
    border-top: 1px solid ${colors.neutralDark};
`;

/* ── Desktop Table ─────────────────────────────── */

const DesktopTable = styled.table`
    width: 100%;
    border-collapse: collapse;
    display: none;

    @media screen and (min-width: ${breakpoints.lg}) {
        display: table;
    }
`;

const Th = styled.th`
    text-align: left;
    padding: 14px 16px;
    font-size: ${fonts.sizes.small};
    font-weight: 600;
    color: ${colors.night};
    text-transform: uppercase;
    letter-spacing: 0.4px;
    border-bottom: 1px solid ${colors.neutralDark};
    white-space: nowrap;

    &:first-child {
        padding-left: 24px;
    }

    &:last-child {
        padding-right: 24px;
    }
`;

const Tr = styled.tr`
    transition: background 0.15s ease;

    &:not(:last-child) {
        border-bottom: 1px solid ${colors.neutralDark};
    }

    &:hover {
        background: ${colors.neutral};
    }
`;

const Td = styled.td`
    padding: 14px 16px;
    vertical-align: middle;

    &:first-child {
        padding-left: 24px;
    }

    &:last-child {
        padding-right: 24px;
    }
`;

/* ── Cell Content ──────────────────────────────── */

const DescriptionCell = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
`;

const DescriptionContent = styled.div`
    display: flex;
    flex-direction: column;
`;

const DescriptionText = styled.span`
    font-size: 14px;
    font-weight: 600;
    color: ${colors.nightDark};
`;

const DateSecondary = styled.span`
    font-size: ${fonts.sizes.small};
    color: ${colors.night};
    margin-top: 2px;
`;

const ValueText = styled.span`
    font-size: 14px;
    color: ${({ color }) => color || colors.nightDark};
`;

const IconCircle = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    width: 36px;
    height: 36px;
    min-width: 36px;
    transition: transform 0.15s ease;

    ${({ category }) => category === 'sell' && `
        background-color: #DCFCE7;
        & > svg { stroke: ${colors.success}; }
    `}

    ${({ category }) => category === 'refund' && `
        background-color: ${colors.danger}12;
        & > svg { stroke: ${colors.dangerDark}; }
    `}

    ${({ category }) => category === 'withdraw' && `
        background-color: ${colors.primary}12;
        & > svg { stroke: ${colors.primary}; }
    `}
`;

const StatusBadge = styled.span`
    display: inline-flex;
    align-items: center;
    padding: 4px 12px;
    border-radius: 50px;
    font-size: ${fonts.sizes.small};
    font-weight: 600;
    letter-spacing: 0.2px;
    white-space: nowrap;
    background: ${({ bgColor }) => bgColor};
    color: ${({ textColor }) => textColor};
`;

/* ── Mobile Cards ──────────────────────────────── */

const MobileCards = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding: 12px 16px;

    @media screen and (min-width: ${breakpoints.lg}) {
        display: none;
    }
`;

const MobileCard = styled.div`
    border: 1px solid ${colors.neutralDark};
    border-radius: 10px;
    padding: 16px;
    transition: all 0.15s ease;

    &:hover {
        background: ${colors.neutral};
        border-color: ${colors.nightLight};
    }
`;

const MobileCardHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 12px;
    padding-bottom: 12px;
    border-bottom: 1px solid ${colors.neutralDark};
`;

const MobileCardBody = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const MobileField = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
`;

const MobileLabel = styled.span`
    font-size: ${fonts.sizes.small};
    font-weight: 600;
    color: ${colors.night};
`;

const MobileValue = styled.span`
    font-size: 13px;
    color: ${colors.nightDark};
`;
